// Presentation-only regrouping of the Settings menu.
//
// The DB (Menu / AccessLevelMst / AccessLevelTran) stays the single source of truth for WHAT a
// user may see -- this file only decides how the items the API already returned are grouped,
// labelled and ordered. Nothing here can grant access: useSettingsNav only ever arranges items
// that came back from /api/AccessLevel/GetMenuHierarchical.
//
// Keys are the existing menuUrl values, so MenuId / MenuUrl / ParentMenuId / routes and the
// access checks in Routes/PrivateRoute.jsx are all untouched and bookmarks keep working.

import PeopleIcon from "@mui/icons-material/People";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PublicIcon from "@mui/icons-material/Public";
import SettingsIcon from "@mui/icons-material/Settings";
import WidgetsIcon from "@mui/icons-material/Widgets";

// Three menu rows share menuUrl "/" (State Health Inspections, and the inactive Draft Manual CEU
// and Campaign), so menuUrl alone is not a unique key. Fall back to the trimmed menu name --
// "Certificates " carries a trailing space in the DB, hence the trim on both sides.
export const menuKey = (item) => {
  const url = item?.menuUrl?.trim();
  if (url && url !== "/") return url;
  return `name:${item?.menuName?.trim() || ""}`;
};

export const GROUP_META = {
  people: {
    name: "People & Access",
    desc: "Who is in the system and what they are allowed to do",
    icon: PeopleIcon,
    color: "#1ebec8",
  },
  training: {
    name: "Courses & Training",
    desc: "Course content, quizzes, pathways and certificates",
    icon: MenuBookIcon,
    color: "#f79153",
  },
  compliance: {
    name: "CEUs & Compliance",
    desc: "Continuing education credit and audit records",
    icon: WorkspacePremiumIcon,
    color: "#86c65a",
  },
  districts: {
    name: "Districts & Sites",
    desc: "Districts, school sites and their configuration",
    icon: BusinessIcon,
    color: "#6c5ce7",
  },
  communication: {
    name: "Communication",
    desc: "Email, notifications and campaigns",
    icon: EmailIcon,
    color: "#1ebec8",
  },
  evaluations: {
    name: "Evaluations",
    desc: "Staff evaluation questions, setup and reporting",
    icon: FactCheckIcon,
    color: "#fa4747",
  },
  website: {
    name: "Website & Content",
    desc: "Public marketing site pages and recipe content",
    icon: PublicIcon,
    color: "#f79153",
  },
  system: {
    name: "System",
    desc: "App-wide configuration, rarely changed",
    icon: SettingsIcon,
    color: "#36454f",
  },
  // Trailing catch-all. Any menu row added to the DB later lands here instead of disappearing.
  more: {
    name: "More Settings",
    desc: "Additional settings available to your account",
    icon: WidgetsIcon,
    color: "#36454f",
  },
};

// Render order of the groups. "more" is deliberately last.
export const GROUP_ORDER = [
  "people",
  "training",
  "compliance",
  "districts",
  "communication",
  "evaluations",
  "website",
  "system",
  "more",
];

// menuKey -> group id. Order within each group below is the order shown in the UI.
export const ITEM_GROUPS = {
  // --- People & Access ---
  "hrSetting/user": "people",
  "hrSetting/userGroup": "people",
  "hrSetting/jobTitle": "people",
  "hrSetting/reportingManager": "people",
  "generalSetting/role": "people",
  "generalSetting/accessLevels": "people",
  "generalSetting/hierarchicalChart": "people",
  "schoolSetting/assignSite_Locations": "people",
  "schoolSetting/communityAccess": "people",

  // --- Courses & Training ---
  "courseSetting/courses": "training",
  "courseSetting/districtCourses": "training",
  "courseSetting/partnerCourses": "training",
  "courseSetting/courseCategory": "training",
  "courseSetting/courseLable": "training",
  "courseSetting/quizzes": "training",
  "courseSetting/quizAssessment": "training",
  "courseSetting/learningPaths": "training",
  "courseSetting/certificate": "training",
  "courseSetting/courseSchedule": "training",
  "courseSetting/manualCourseScheduled": "training",
  "courseSetting/groupVideoTraining": "training",

  // --- CEUs & Compliance ---
  "courseSetting/manualCEU": "compliance",
  "courseSetting/multiUserFIGCEU": "compliance",
  "courseSetting/deleteSystemGeneratedCEU": "compliance",
  "generalSetting/academicYear": "compliance",
  "name:Draft Manual CEU": "compliance",
  "name:State Health Inspections": "compliance",

  // --- Districts & Sites ---
  "schoolSetting/districtManagement": "districts",
  "schoolSetting/Site_Location": "districts",
  "mainSiteSetting/districtMap": "districts",
  "schoolSetting/districtPaymentStripeAccount": "districts",
  "schoolSetting/goalCategory": "districts",
  "schoolSetting/freeSource": "districts",
  "schoolSetting/freeSourcesCategory": "districts",

  // --- Communication ---
  // Email Center has no Menu row today, so it only ever appears if one is added.
  "figCommunication/emailCenter": "communication",
  "figCommunication/Templates": "communication",
  "generalSetting/automatedEmail": "communication",
  "figCommunication/sendMail": "communication",
  "figCommunication/sendCommunicationEmail": "communication",
  "figCommunication/smtpConfig": "communication",
  "generalSetting/pushNotification": "communication",
  "generalSetting/eventPromotion": "communication",
  "name:Campaign": "communication",

  // --- Evaluations ---
  "evaluation/evaluationGroup": "evaluations",
  "evaluation/evaluationQuestion": "evaluations",
  "evaluation/evaluationSetup": "evaluations",
  "evaluation/evaluationEmployee": "evaluations",
  "evaluation/evaluationReport": "evaluations",

  // --- Website & Content ---
  "mainSiteSetting/homePage": "website",
  "mainSiteSetting/aboutUsPage": "website",
  "mainSiteSetting/contactUsPage": "website",
  "mainSiteSetting/footerPage": "website",
  "mainSiteSetting/whatWeDo(Boxes)": "website",
  "mainSiteSetting/coreValues(Boxes)": "website",
  "mainSiteSetting/webinar": "website",
  "mainSiteSetting/webinarJoinRequest": "website",
  "mainSiteSetting/demonstration": "website",
  "mainSiteSetting/proposal": "website",
  "recipeSetting/recipeManagement": "website",
  "recipeSetting/recipeCategory": "website",
  "recipeSetting/recipeSubCategory": "website",
  "generalSetting/Block&Report": "website",

  // --- System ---
  "generalSetting/Menu": "system",
  "generalSetting/appVersionUpdate": "system",
  "generalSetting/aiPromptTexts": "system",
  "https://ai.swancreate.com/usda-fdc": "system",
};

// Shorter, plainer labels. The DB menu name is used when there is no override here.
export const ITEM_LABELS = {
  "hrSetting/user": "Users",
  "hrSetting/userGroup": "User Groups",
  "hrSetting/jobTitle": "Job Titles",
  "generalSetting/role": "Roles",
  "generalSetting/hierarchicalChart": "Org Chart",
  "courseSetting/certificate": "Certificates",
  "courseSetting/courseLable": "Course Labels",
  "courseSetting/multiUserFIGCEU": "Multi-User FIG CEU",
  "courseSetting/deleteSystemGeneratedCEU": "Delete System CEU",
  "schoolSetting/districtPaymentStripeAccount": "Stripe Payment Setup",
  "figCommunication/Templates": "Email Templates",
  "figCommunication/smtpConfig": "SMTP Configuration",
  "generalSetting/aiPromptTexts": "AI Prompt Texts",
  "mainSiteSetting/whatWeDo(Boxes)": "What We Do",
  "mainSiteSetting/coreValues(Boxes)": "Core Values",
};

export const groupIdFor = (key) => ITEM_GROUPS[key] || "more";
export const labelFor = (key, fallback) =>
  ITEM_LABELS[key] || fallback?.trim() || key;
